/**
 * 图片滤镜插件的公开契约
 *
 * 用于 FilterList 事件订阅以及 user-editor 注入画布合并事务，不包含任何 UI 实现。
 */
import type { ILayerProps } from '@/version-types';

/** 图片滤镜插件注册实例名。 */
export const IMAGE_FILTER_PLUGIN_KEY = 'ImageFilterPlugin';

/** 图片滤镜自定义历史类型。 */
export const IMAGE_FILTER_HISTORY_TYPE = 'applyFilter';

/** 图片滤镜插件事件。 */
export enum ImageFilterEvent {
    /** UI 可见状态发生变化。 */
    STATE_CHANGE = 'image-filter/state-change',
    /** 滤镜结果提交成功。 */
    APPLIED = 'image-filter/applied',
    /** 当前滤镜草稿已取消。 */
    CANCELLED = 'image-filter/cancelled',
    /** 滤镜命令执行失败。 */
    ERROR = 'image-filter/error',
}

/** 图片滤镜命令状态。 */
export type ImageFilterStatus = 'idle' | 'loading' | 'ready' | 'applying' | 'error';

/** UI 当前选中的滤镜。 */
export interface IImageFilterSelection {
    /** 滤镜素材 ID。 */
    filterId: string;
    /** 滤镜分类 ID。 */
    categoryId: string;
}

/** 分发给 UI 的只读滤镜状态。 */
export interface IImageFilterUISnapshot {
    /** 当前会话图层 ID。 */
    layerId: string;
    /** 当前选中滤镜；null 表示原图。 */
    filter: IImageFilterSelection | null;
    /** 滤镜强度，范围 0-1。 */
    alpha: number;
    /** 当前命令状态。 */
    status: ImageFilterStatus;
    /** 是否存在尚未提交的滤镜草稿。 */
    hasPendingChanges: boolean;
    /** 是否正在显示滤镜临时资源。 */
    isPreviewing: boolean;
    /** 最近一次错误信息。 */
    error: string;
}

/** 选择滤镜命令参数。 */
export interface ISelectImageFilterOptions {
    /** 滤镜素材 ID。 */
    filterId: string;
    /** 滤镜分类 ID。 */
    categoryId: string;
    /** 离线滤镜资源地址。 */
    filterUrl: string;
}

/** 滤镜结果提交上下文。 */
export interface IImageFilterCommitContext {
    /** 提交前的正式图层。 */
    oldLayer: ILayerProps;
    /** 写入滤镜 URL 后的新图层。 */
    newLayer: ILayerProps;
    /** 打开当前会话时的图层快照。 */
    originLayer: ILayerProps;
    /** 可选历史统计标识。 */
    trackFunc?: string;
}

/** 由业务宿主提供的外部事务回调。 */
export interface IImageFilterHostOptions {
    /**
     * 提交滤镜结果
     *
     * 返回 true 表示宿主已提交且已处理历史；返回 false 时插件执行普通图层提交和滤镜历史。
     *
     * @param context - 滤镜结果及提交前后的图层快照
     * @returns 提交完成后的异步结果
     * 副作用：可由业务更新舞台并写入替换历史。
     */
    commit?: (context: IImageFilterCommitContext) => boolean | Promise<boolean>;
    /**
     * 回滚宿主管理的临时事务
     *
     * @returns 回滚完成后的异步结果
     * 副作用：可由业务退出 MainLayer 合并画布模式。
     */
    rollback?: () => void | Promise<void>;
}

/** 滤镜应用结果。 */
export interface IImageFilterApplyResult {
    /** 提交前的正式图层。 */
    oldLayer: ILayerProps;
    /** 已生成滤镜 URL 的结果图层。 */
    newLayer: ILayerProps;
    /** 应用的滤镜 ID。 */
    filterId: string;
    /** 应用的滤镜分类 ID。 */
    categoryId: string;
    /** 应用强度。 */
    alpha: number;
}

/** 滤镜取消结果。 */
export interface IImageFilterCancelResult {
    /** 取消草稿的图层 ID。 */
    layerId: string;
    /** 取消前选中的滤镜 ID。 */
    filterId: string;
    /** 取消前选中的滤镜分类 ID。 */
    categoryId: string;
    /** 取消前的滤镜强度。 */
    alpha: number;
}

/** 图片滤镜历史数据。 */
export interface IImageFilterHistoryData {
    /** 单次滤镜提交数据。 */
    item: {
        /** 提交前的正式图层。 */
        oldLayer: ILayerProps;
        /** 提交后的正式图层。 */
        newLayer: ILayerProps;
        /** 协同或重基产生的属性差异。 */
        diffInfo?: Record<string, object>;
    };
}

/** 滤镜状态监听器。 */
export type ImageFilterStateListener = (snapshot: IImageFilterUISnapshot) => void;
